/* eslint-disable no-param-reassign, no-shadow, no-console */
import * as Sentry from '@sentry/vue'
import Config from '@/config'
import slideApi from '@/api/slide'
import { resolveMarketplaceBase } from '@/utils/resolveMarketplaceBase'
// AHA-47349-presenter-can-create-new-slide-type
import { fetchDeveloperSlideTypes, fetchPublishedSlideTypes } from '@/api/developerManifest'

// Hard cap on how many slide types a user can pin to "My slide types". The
// picker gates new pins against this (with a toast) before they ever reach the
// store; this slice is the last-resort backstop so the persisted list can never
// exceed it even if a caller bypasses that gate.
export const MAX_PINNED = 20

// The catalogue lives on an external host (Cloudflare Pages). Right after a fresh
// deploy its edge cache is cold, so the first request can hang or fail — and with
// no recovery the "My slide types" skeleton would stay forever (it only cleared on
// a tab-return refetch). Abort each attempt after a bounded time so `isLoading`
// always resolves, and retry a few times: when the first attempt times out the
// next one usually hits a now-warm instance. No artificial backoff — the timeout
// itself spaces out attempts in the hang case, which is the common failure.
const FETCH_TIMEOUT_MS = 8000
const MAX_FETCH_ATTEMPTS = 3
// Linear backoff between attempts (500ms, then 1000ms) so a transient 5xx /
// network blip isn't burned through in three instant retries.
const FETCH_RETRY_BASE_DELAY_MS = 500
// Published slide types load in the background; after this many failed attempts
// the session stops retrying so a missing/broken endpoint isn't hit on every open.
const MAX_PUBLISHED_FETCH_ATTEMPTS = 3

const toApiSlugs = (slugs) => {
  return slugs
}

// The base is resolved per call rather than at module load so a `?marketplaceSandboxId=`
// override applied on this page load is picked up without a rebuild. On any non-sandbox
// build this returns the build-time `Config.ahaslideMarketplaceUrl` unchanged.
// No per-user params: the URL is the edge cache key, so it must be identical for every user.
const buildSlideTypesUrl = () => {
  const base = resolveMarketplaceBase(
    Config.ahaslideMarketplaceUrl,
    Config.presenterDomain,
  )
  return `${base}/api/slide-types?env=${encodeURIComponent(Config.env)}`
}

// One catalogue request with a hard timeout. Throws on non-OK status, network
// error, or timeout (abort), so the caller can retry / fall back.
const fetchSlideTypesOnce = async (url) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null
  try {
    const response = await fetch(url, controller ? { signal: controller.signal } : undefined)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    const rawTypes = Array.isArray(data) ? data : (data.slideTypes || [])
    return rawTypes.map((slide) => {
      if (!slide) return null
      return slide.ahaConfig ? { ...slide.ahaConfig, ...slide } : slide
    }).filter(Boolean)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

const state = () => ({
  slideTypes: [],
  isLoading: false,
  error: null,
  pinnedSlugs: [],
  fetchPromise: null,
  // AHA-47349-presenter-can-create-new-slide-type — which userId we've already
  // appended developer slide types for (so we do it once per user, and not
  // during the Unleash-ready pre-fetch that can run before currentUser loads).
  developerFetchedForUserId: null,
  // Published slide types (every creator, not flag-gated) on the flag-off path:
  // appended once per session, one request in flight at a time, capped retries.
  publishedAppended: false,
  publishedFetchPromise: null,
  publishedFailedAttempts: 0,
})

// Feature-flag gate applied at the data source. A slide type may ship an
// optional `featureFlagKey` (from its marketplace `ahaConfig`, e.g. Draw Answer
// → `slideTypeMarket-draw-answer`) to roll out behind an Unleash flag. When a
// slide carries one, its `visible` is driven off that flag — enabled →
// visible:true, disabled → visible:false — so EVERY consumer of this getter
// (the picker's `visibleSlideTypes`, the marketplace search list's
// `filteredSlideTypes`, icon/typeObj resolution) sees the same corrected value.
// Slides WITHOUT a `featureFlagKey` are returned untouched, keeping their
// API-provided `visible`. This is reactive: reading the featureToggles getter
// inside this getter re-evaluates it whenever the flags load or change.
const applyFeatureFlagVisibility = (slideTypes, isFeatureEnabled) => {
  if (typeof isFeatureEnabled !== 'function') return slideTypes
  return slideTypes.map((slide) => {
    if (!slide) return slide
    const flagKey = slide.featureFlagKey || (slide.ahaConfig && slide.ahaConfig.featureFlagKey)
    if (!flagKey) return slide
    // Coerce to a strict boolean: the real `featureToggles/isFeatureEnabled`
    // getter returns `undefined` (not `false`) for a flag that doesn't exist yet,
    // and the picker/search filters test `visible === false` strictly — so an
    // unset flag would leak through as visible. Boolean() makes it a hard false.
    return { ...slide, visible: Boolean(isFeatureEnabled(flagKey)) }
  })
}

const getters = {
  marketplaceSlideTypes: (state, _getters, _rootState, rootGetters) => {
    return applyFeatureFlagVisibility(
      state.slideTypes,
      rootGetters && rootGetters['featureToggles/isFeatureEnabled'],
    )
  },
  isMarketplaceLoading: (state) => state.isLoading,
  marketplaceError: (state) => state.error,
  pinnedSlugs: (state) => state.pinnedSlugs,
}

// Append the entries the catalogue doesn't already hold (keyed like the picker's pins).
const appendMissing = (state, list) => {
  if (!list || !list.length) return
  const keyOf = (slide) => (slide && (slide.pinKey || slide.type || slide.name)) || ''
  const existing = new Set(state.slideTypes.map(keyOf))
  const additions = list.filter((slide) => slide && !existing.has(keyOf(slide)))
  if (additions.length) state.slideTypes = [...state.slideTypes, ...additions]
}

const mutations = {
  FETCH_SLIDE_TYPES_START(state, promise) {
    state.isLoading = true
    state.error = null
    state.fetchPromise = promise
  },
  FETCH_SLIDE_TYPES_SUCCESS(state, slideTypes) {
    state.isLoading = false
    state.slideTypes = slideTypes
    state.fetchPromise = null
  },
  FETCH_SLIDE_TYPES_FAILURE(state, error) {
    state.isLoading = false
    state.error = error
    state.fetchPromise = null
  },
  SET_PINNED_SLUGS(state, slugs) {
    state.pinnedSlugs = slugs
  },
  // AHA-47349-presenter-can-create-new-slide-type
  APPEND_DEVELOPER_SLIDE_TYPES(state, { userId, devTypes }) {
    state.developerFetchedForUserId = userId
    appendMissing(state, devTypes)
  },
  PUBLISHED_FETCH_START(state, promise) {
    state.publishedFetchPromise = promise
  },
  APPEND_PUBLISHED_SLIDE_TYPES(state, publishedTypes) {
    state.publishedAppended = true
    state.publishedFetchPromise = null
    appendMissing(state, publishedTypes)
  },
  PUBLISHED_FETCH_FAILURE(state) {
    state.publishedFailedAttempts += 1
    state.publishedFetchPromise = null
  },
  // Swap the whole developer/published slice for a freshly fetched one, e.g. after
  // a publish / unpublish on the Developer page, so the picker drops what was
  // unpublished and gains what was published without a page reload.
  REPLACE_DEVELOPER_SLIDE_TYPES(state, { userId, devTypes }) {
    state.developerFetchedForUserId = userId
    const firstParty = state.slideTypes.filter((slide) => !(slide && slide.isDeveloperSlideType))
    state.slideTypes = [...firstParty, ...(devTypes || [])]
  },
}

// Published slide types are open to every presenter, so they are appended even
// when the create-slide-type flag is off (only the Developer page and the user's
// own drafts stay behind it). No userId needed. Concurrent callers share the
// in-flight request; a failure is retried by a later catalogue dispatch, up to
// MAX_PUBLISHED_FETCH_ATTEMPTS. Never rejects.
const ensurePublishedAppended = ({ commit, state }) => {
  if (state.publishedAppended) return Promise.resolve()
  if (state.publishedFetchPromise) return state.publishedFetchPromise
  if (state.publishedFailedAttempts >= MAX_PUBLISHED_FETCH_ATTEMPTS) return Promise.resolve()
  const promise = (async () => {
    try {
      const publishedTypes = await fetchPublishedSlideTypes({ strict: true })
      commit('APPEND_PUBLISHED_SLIDE_TYPES', publishedTypes)
    } catch (e) {
      // best-effort; never break the catalogue
      commit('PUBLISHED_FETCH_FAILURE')
    }
  })()
  commit('PUBLISHED_FETCH_START', promise)
  return promise
}

// AHA-47349-presenter-can-create-new-slide-type — append the slide types the
// user has created (from the backend's public user-manifests endpoint) to the
// catalogue, ONCE the user is known. Runs even when the base catalogue was
// already cached, because the Unleash-ready pre-fetch can populate it before
// currentUser loads. Flag off => published types only; missing userId / already
// done / any failure => no-op.
const ensureDeveloperAppended = async ({
  commit, state, rootGetters, rootState,
}) => {
  if (!rootGetters['featureToggles/canUseCreateNewSlideType']) {
    // Not awaited: the picker must never wait on this. The mutation adds the
    // published types to the catalogue whenever the request lands.
    ensurePublishedAppended({ commit, state })
    return
  }
  const userId = rootState && rootState.currentUser && rootState.currentUser.id
  if (!userId) return
  if (state.developerFetchedForUserId === userId) return
  try {
    // The user's own drafts, then every published slide type: same shape, one list.
    const [devTypes, publishedTypes] = await Promise.all([
      fetchDeveloperSlideTypes({ userId }),
      fetchPublishedSlideTypes(),
    ])
    commit('APPEND_DEVELOPER_SLIDE_TYPES', { userId, devTypes: [...devTypes, ...publishedTypes] })
  } catch (e) {
    // best-effort; never break the catalogue
  }
}

const actions = {
  // AHA-47349 — re-fetch the user's drafts + every published type (past the 60s
  // cache) and replace them in the catalogue. Best-effort: the fetches are strict,
  // so if EITHER fails nothing is committed and the catalogue keeps what it had
  // (a replace with a failure's [] would wipe the developer slice). Skipped until
  // the base catalogue is loaded: the regular append covers that first load, and
  // seeding an empty catalogue with dev types only would make
  // fetchMarketplaceSlideTypes skip the first-party fetch.
  async refreshDeveloperSlideTypes({
    commit, state, rootGetters, rootState,
  }) {
    if (!rootGetters['featureToggles/canUseCreateNewSlideType']) return
    const userId = rootState && rootState.currentUser && rootState.currentUser.id
    if (!userId) return
    if (state.fetchPromise) {
      try {
        await state.fetchPromise
      } catch (e) {
        return
      }
    }
    if (!state.slideTypes.length) return
    try {
      const [devTypes, publishedTypes] = await Promise.all([
        fetchDeveloperSlideTypes({ userId, fresh: true, strict: true }),
        fetchPublishedSlideTypes({ fresh: true, strict: true }),
      ])
      commit('REPLACE_DEVELOPER_SLIDE_TYPES', { userId, devTypes: [...devTypes, ...publishedTypes] })
    } catch (e) {
      // best-effort; never break the catalogue
    }
  },
  async fetchMarketplaceSlideTypes({
    commit,
    state,
    rootGetters,
    rootState,
  }) {
    // Feature-flag isolation: this action is a no-op unless the marketplace flag
    // OR the data-driven slide picker is on, so the store is never populated for
    // users on neither flag and every consumer (icons, typeObj resolution,
    // pickers) falls back to original behaviour. All callers may dispatch
    // unconditionally; the gate lives here. The data-driven picker resolves its
    // server pinKeys (including plugin types) against this catalogue, so it needs
    // the feed even when the marketplace flag is off — otherwise plugin types
    // silently drop to native-only.
    const marketplaceEnabled = rootGetters['featureToggles/canUseNewSlideMarketPlace']
    const dataDrivenPickerEnabled = rootGetters['featureToggles/canUseDataDrivenSlidePicker']
    if (!marketplaceEnabled && !dataDrivenPickerEnabled) return Promise.resolve()
    if (state.slideTypes.length > 0) {
      // AHA-47349-presenter-can-create-new-slide-type — the base catalogue is
      // already cached, but the developer append may have been skipped earlier
      // (the pre-fetch ran before currentUser was known). Do it now.
      await ensureDeveloperAppended({
        commit, state, rootGetters, rootState,
      })
      return Promise.resolve()
    }
    if (state.fetchPromise) {
      // AHA-47349-presenter-can-create-new-slide-type — a fetch is already in
      // flight (typically the Unleash-ready pre-fetch, which ran before currentUser
      // loaded so its own append no-op'd). Append after it resolves; keyed per
      // userId in ensureDeveloperAppended so the extra call is idempotent.
      return state.fetchPromise.then(async () => {
        await ensureDeveloperAppended({
          commit, state, rootGetters, rootState,
        })
        return true
      })
    }

    const promise = (async () => {
      const url = buildSlideTypesUrl()
      let lastError
      for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const fetchedTypes = await fetchSlideTypesOnce(url)
          commit('FETCH_SLIDE_TYPES_SUCCESS', fetchedTypes)
          // AHA-47349-presenter-can-create-new-slide-type — append the slide
          // type(s) the user has created (flag + userId gated).
          // eslint-disable-next-line no-await-in-loop
          await ensureDeveloperAppended({
            commit, state, rootGetters, rootState,
          })
          return
        } catch (e) {
          lastError = e
          if (attempt < MAX_FETCH_ATTEMPTS) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => {
              setTimeout(resolve, FETCH_RETRY_BASE_DELAY_MS * attempt)
            })
          }
        }
      }
      console.error('Failed to fetch marketplace slide types:', lastError)
      // Without the catalogue every marketplace slide renders blank, so this must be countable.
      Sentry.captureException(lastError, {
        fingerprint: ['slide-marketplace-catalogue-load-failed'],
        tags: { feature: 'slide-marketplace' },
        extra: { url, attempts: MAX_FETCH_ATTEMPTS },
      })
      commit('FETCH_SLIDE_TYPES_FAILURE', (lastError && lastError.message) || 'Unknown error')
    })()

    commit('FETCH_SLIDE_TYPES_START', promise)

    return promise
  },

  async setPinnedDefaults({ commit }, slugs) {
    commit('SET_PINNED_SLUGS', slugs)
    try {
      await slideApi.upsertPinnedSlideTypes(toApiSlugs(slugs))
    } catch (e) {
      console.error('Failed to seed default pinned slide types:', e)
      throw e
    }
  },

  async fetchPinnedSlideTypes({ commit }) {
    try {
      const slugs = await slideApi.getPinnedSlideTypes()
      commit('SET_PINNED_SLUGS', Array.isArray(slugs) ? slugs : [])
    } catch (e) {
      console.error('Failed to fetch pinned slide types:', e)
    }
  },

  // Payload is `{ addSlug }` to pin or `{ removeSlugs }` to unpin. The caller
  // resolves which existing slugs match the item (using the same fuzzy matching
  // as the pinned-state display), so an item pinned under an equivalent slug
  // (e.g. Content stored as `content` but typed `freestyle`) can still be
  // unpinned instead of accidentally adding a second, never-removable slug.
  async togglePinSlideType({ commit, state }, payload) {
    const current = state.pinnedSlugs
    let next
    if (payload.removeSlugs && payload.removeSlugs.length) {
      const removeSet = new Set(payload.removeSlugs)
      next = current.filter((s) => !removeSet.has(s))
    } else {
      const slug = payload.addSlug
      next = current.includes(slug) ? current : [...current, slug].slice(0, MAX_PINNED)
    }
    commit('SET_PINNED_SLUGS', next)

    try {
      await slideApi.upsertPinnedSlideTypes(toApiSlugs(next))
    } catch (e) {
      console.error('Failed to update pinned slide types:', e)
      commit('SET_PINNED_SLUGS', current)
      throw e
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
